/**
 * Utility functions for camp date formatting, effective close dates, and registration windows.
 */

import { hasEarlyAccessForProgram } from './eligibility';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Groups consecutive dates into structured range objects.
 * Consecutive calendar days are combined into single spans (e.g., 08/14-08/21/2027).
 *
 * @param {string[]|string} dates
 * @returns {Array<{ label: string, count: number, subtitle: string|null, raw: string[] }>}
 */
export const groupCampDates = (dates) => {
    if (!dates) return [];
    if (typeof dates === 'string') return [{ label: dates, count: 1, subtitle: null, raw: [dates] }];
    if (!Array.isArray(dates) || dates.length === 0) return [];

    const parseDateToUTC = (d) => {
        if (!d) return null;
        if (typeof d === 'string') {
            const isoMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (isoMatch) {
                const year = parseInt(isoMatch[1], 10);
                const month = parseInt(isoMatch[2], 10);
                const day = parseInt(isoMatch[3], 10);
                return {
                    year, month, day,
                    time: Date.UTC(year, month - 1, day),
                    raw: d
                };
            }
            if (d.includes('/')) {
                const parts = d.split('/');
                if (parts.length === 3) {
                    const month = parseInt(parts[0], 10);
                    const day = parseInt(parts[1], 10);
                    const year = parseInt(parts[2], 10);
                    return {
                        year, month, day,
                        time: Date.UTC(year, month - 1, day),
                        raw: d
                    };
                }
            }
        }
        const t = Date.parse(d);
        if (!isNaN(t)) {
            const dt = new Date(t);
            return {
                year: dt.getFullYear(),
                month: dt.getMonth() + 1,
                day: dt.getDate(),
                time: dt.getTime(),
                raw: d
            };
        }
        return null;
    };

    const parsedDates = [];
    const unparseable = [];

    for (const d of dates) {
        const parsed = parseDateToUTC(d);
        if (parsed) {
            parsedDates.push(parsed);
        } else if (d) {
            unparseable.push(d);
        }
    }

    parsedDates.sort((a, b) => a.time - b.time);

    if (parsedDates.length === 0) {
        return unparseable.map(u => ({ label: String(u), count: 1, subtitle: null, raw: [u] }));
    }

    const isNextDay = (a, b) => {
        const diffDays = Math.round((b.time - a.time) / (1000 * 3600 * 24));
        return diffDays === 1;
    };

    const groups = [];
    let currentGroup = [parsedDates[0]];

    for (let i = 1; i < parsedDates.length; i++) {
        const curr = parsedDates[i];
        const prev = currentGroup[currentGroup.length - 1];

        if (isNextDay(prev, curr)) {
            currentGroup.push(curr);
        } else {
            groups.push(currentGroup);
            currentGroup = [curr];
        }
    }
    groups.push(currentGroup);

    const formatSingle = (item) => {
        if (item.raw && item.raw.includes('/')) return item.raw;
        const m = String(item.month).padStart(2, '0');
        const d = String(item.day).padStart(2, '0');
        return `${m}/${d}/${item.year}`;
    };

    const result = groups.map(group => {
        if (group.length === 1) {
            return {
                label: formatSingle(group[0]),
                count: 1,
                subtitle: null,
                raw: [group[0].raw]
            };
        }
        const first = group[0];
        const last = group[group.length - 1];
        const m1 = String(first.month).padStart(2, '0');
        const d1 = String(first.day).padStart(2, '0');
        const m2 = String(last.month).padStart(2, '0');
        const d2 = String(last.day).padStart(2, '0');
        const y2 = last.year;
        const label = `${m1}/${d1}-${m2}/${d2}/${y2}`;
        return {
            label,
            count: group.length,
            subtitle: `${group.length} days`,
            raw: group.map(g => g.raw)
        };
    });

    for (const u of unparseable) {
        result.push({ label: String(u), count: 1, subtitle: null, raw: [u] });
    }

    return result;
};

/**
 * Formats a list of camp or program dates into concise, human-friendly spans.
 * Consecutive calendar days are grouped into MM/DD-MM/DD/YYYY spans.
 * If maxSpans is provided and the number of groups exceeds maxSpans, returns an overall date range with session count.
 *
 * @param {string[]|string} dates
 * @param {Object} [options]
 * @param {number} [options.maxSpans]
 * @returns {string}
 */
export const formatCampDates = (dates, options = {}) => {
    const { maxSpans = 0 } = options;
    if (!dates) return 'No dates';
    if (typeof dates === 'string') return dates;
    if (!Array.isArray(dates) || dates.length === 0) return 'No dates';

    const grouped = groupCampDates(dates);
    if (grouped.length === 0) return 'No dates';

    // If maxSpans is configured and distinct groups exceed limit, return clean overall range with session count
    if (maxSpans > 0 && grouped.length > maxSpans) {
        const parseDateToUTC = (d) => {
            if (!d) return null;
            if (typeof d === 'string') {
                const isoMatch = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
                if (isoMatch) {
                    const year = parseInt(isoMatch[1], 10);
                    const month = parseInt(isoMatch[2], 10);
                    const day = parseInt(isoMatch[3], 10);
                    return { year, month, day, time: Date.UTC(year, month - 1, day) };
                }
                if (d.includes('/')) {
                    const parts = d.split('/');
                    if (parts.length === 3) {
                        return {
                            year: parseInt(parts[2], 10),
                            month: parseInt(parts[0], 10),
                            day: parseInt(parts[1], 10),
                            time: Date.UTC(parseInt(parts[2], 10), parseInt(parts[0], 10) - 1, parseInt(parts[1], 10))
                        };
                    }
                }
            }
            const t = Date.parse(d);
            if (!isNaN(t)) {
                const dt = new Date(t);
                return { year: dt.getFullYear(), month: dt.getMonth() + 1, day: dt.getDate(), time: dt.getTime() };
            }
            return null;
        };

        const parsedDates = dates.map(parseDateToUTC).filter(Boolean).sort((a, b) => a.time - b.time);
        if (parsedDates.length > 0) {
            const first = parsedDates[0];
            const last = parsedDates[parsedDates.length - 1];
            const count = parsedDates.length;
            const countLabel = count === 1 ? '1 date' : `${count} sessions`;
            const startMonth = MONTHS_SHORT[first.month - 1];
            const endMonth = MONTHS_SHORT[last.month - 1];

            let rangeStr = '';
            if (first.year === last.year) {
                if (first.month === last.month) {
                    rangeStr = `${startMonth} ${first.day} – ${last.day}, ${first.year}`;
                } else {
                    rangeStr = `${startMonth} ${first.day} – ${endMonth} ${last.day}, ${first.year}`;
                }
            } else {
                rangeStr = `${startMonth} ${first.day}, ${first.year} – ${endMonth} ${last.day}, ${last.year}`;
            }
            return `${rangeStr} (${countLabel})`;
        }
    }

    return grouped.map(g => g.label).join(', ');
};

/**
 * Formats a date string (YYYY-MM-DD or ISO string) to readable format e.g. "September 1, 2026".
 * @param {string} dateStr 
 * @returns {string}
 */
export const formatDateString = (dateStr) => {
    if (!dateStr) return 'a later date';
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
        const [, year, month, day] = match;
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
        return d.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    }
    return dateStr;
};

export const normalizeDateKey = (rawDate) => {
    if (!rawDate) return null;
    if (rawDate instanceof Date && !isNaN(rawDate.getTime())) {
        return rawDate.toLocaleDateString('en-CA');
    }
    if (typeof rawDate?.toDate === 'function') {
        const dt = rawDate.toDate();
        if (dt instanceof Date && !isNaN(dt.getTime())) {
            return dt.toLocaleDateString('en-CA');
        }
    }
    if (typeof rawDate !== 'string') return null;

    const isoMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }
    if (rawDate.includes('/')) {
        const parts = rawDate.split('/');
        if (parts.length === 3) {
            const m = parts[0].padStart(2, '0');
            const d = parts[1].padStart(2, '0');
            const y = parts[2];
            return `${y}-${m}-${d}`;
        }
    }
    const parsed = Date.parse(rawDate);
    if (!isNaN(parsed)) {
        return new Date(parsed).toLocaleDateString('en-CA');
    }
    return null;
};

/**
 * Resolves the effective close date for a program.
 * If program.registrationCloseDate is specified, returns that date.
 * If omitted and the program has dates, returns the latest scheduled date
 * (formatted as YYYY-MM-DD string or Date object).
 *
 * @param {Object} program
 * @returns {string|null} YYYY-MM-DD or null
 */
export const getProgramEffectiveCloseDate = (program) => {
    if (!program) return null;
    if (program.registrationCloseDate) return program.registrationCloseDate;

    if (!Array.isArray(program.dates) || program.dates.length === 0) {
        return null;
    }

    let latestDateStr = null;
    for (const rawDate of program.dates) {
        const dateKey = normalizeDateKey(rawDate);
        if (dateKey && (/^\d{4}-\d{2}-\d{2}$/.test(dateKey))) {
            if (!latestDateStr || dateKey > latestDateStr) {
                latestDateStr = dateKey;
            }
        }
    }
    return latestDateStr;
};

const isDateInFuture = (dateVal, todayStr) => {
    if (!dateVal) return false;
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        return dateVal > todayStr;
    }
    const parsed = new Date(dateVal);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return !isNaN(parsed.getTime()) && parsed > now;
};

const isDateInPast = (dateVal, todayStr) => {
    if (!dateVal) return false;
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        return dateVal < todayStr;
    }
    const parsed = new Date(dateVal);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return !isNaN(parsed.getTime()) && parsed < now;
};

/**
 * Determines if registration is currently open for a given program.
 * Returns false if:
 * 1. program is falsy
 * 2. program.isRegistrationOpen === false (unless account has early/late access override)
 * 3. program.registrationOpenDate exists and is in the future (unless account has early/late access override)
 * 4. program.registrationCloseDate exists and is in the past (unless account has early/late access override)
 * 
 * @param {Object} program 
 * @param {Object|Array} [userAccountOrEarlyAccessIds=null] Optional account doc or early access IDs
 * @returns {boolean}
 */
export const isRegistrationOpenForProgram = (program, userAccountOrEarlyAccessIds = null) => {
    if (!program || program.isActive === false) return false;

    // Check if account has an early or late access override for this program (Issue #208)
    if (hasEarlyAccessForProgram(program, userAccountOrEarlyAccessIds)) {
        return true;
    }

    // Explicitly closed by admin toggle
    if (program.isRegistrationOpen === false) return false;

    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local timezone

    if (isDateInFuture(program.registrationOpenDate, todayStr)) {
        return false;
    }

    const effectiveCloseDate = getProgramEffectiveCloseDate(program);
    if (isDateInPast(effectiveCloseDate, todayStr)) {
        return false;
    }

    return true;
};

/**
 * Returns true when registration is currently closed solely because the
 * registrationOpenDate has not yet arrived (program is not explicitly disabled
 * and its close date has not already passed).
 *
 * Use this to distinguish "not yet open" from "permanently closed" so the UI
 * can render a contextual "Registration opens on <date>" hint.
 *
 * @param {Object} program
 * @param {Object|Array} [userAccountOrEarlyAccessIds=null]
 * @returns {boolean}
 */
export const isRegistrationNotYetOpen = (program, userAccountOrEarlyAccessIds = null) => {
    if (!program) return false;
    if (program.isActive === false) return false;
    if (hasEarlyAccessForProgram(program, userAccountOrEarlyAccessIds)) return false;

    // Must have a registrationOpenDate that is in the future
    if (!program.registrationOpenDate) return false;

    const todayStr = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
    const dateStr = program.registrationOpenDate;

    const isFutureOpenDate =
        typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
            ? dateStr > todayStr
            : (() => {
                const openDate = new Date(dateStr);
                const now = new Date();
                now.setHours(0, 0, 0, 0);
                return !isNaN(openDate.getTime()) && openDate > now;
            })();

    if (!isFutureOpenDate) return false;

    // If there is an effective close date that has already passed, treat as permanently closed
    const effectiveCloseDate = getProgramEffectiveCloseDate(program);
    if (effectiveCloseDate) {
        const isPastClose =
            typeof effectiveCloseDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(effectiveCloseDate)
                ? effectiveCloseDate < todayStr
                : (() => {
                    const closeDate = new Date(effectiveCloseDate);
                    const now = new Date();
                    now.setHours(0, 0, 0, 0);
                    return !isNaN(closeDate.getTime()) && closeDate < now;
                })();
        if (isPastClose) return false;
    }

    return true;
};

/**
 * Returns true when registration is permanently and definitively closed — meaning
 * the program is inactive, explicitly disabled, or its effective registration close date
 * (explicit registrationCloseDate or last scheduled date) has already passed.
 * In this state, "Notify Me" notifications will never fire and the program
 * should be hidden from registration selection lists.
 *
 * Distinct from isRegistrationNotYetOpen(), which captures the "pending future
 * open date" state where NotifyMe is still meaningful.
 *
 * @param {Object} program
 * @param {Object|Array} [userAccountOrEarlyAccessIds=null]
 * @returns {boolean}
 */
export const isRegistrationPermanentlyClosed = (program, userAccountOrEarlyAccessIds = null) => {
    if (!program) return true;
    if (program.isActive === false) return true;
    if (hasEarlyAccessForProgram(program, userAccountOrEarlyAccessIds)) return false;
    if (isRegistrationNotYetOpen(program, userAccountOrEarlyAccessIds)) return false;
    if (program.isRegistrationOpen === false) return true;

    const effectiveCloseDate = getProgramEffectiveCloseDate(program);
    if (effectiveCloseDate) {
        const todayStr = new Date().toLocaleDateString('en-CA');

        if (typeof effectiveCloseDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(effectiveCloseDate)) {
            if (effectiveCloseDate < todayStr) return true;
        } else {
            const closeDate = new Date(effectiveCloseDate);
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            if (!isNaN(closeDate.getTime()) && closeDate < now) return true;
        }
    }

    return false;
};

/**
 * Extracts a normalized YYYY-MM-DD or comparable date string representing the start date
 * of a program, camp, event, or race.
 *
 * @param {Object} item
 * @returns {string|null}
 */
export const getProgramStartDateString = (item) => {
    if (!item || typeof item !== 'object') return null;
    const raw = item.startDate || item.date || (Array.isArray(item.dates) && item.dates[0]) || null;
    return normalizeDateKey(raw);
};

const resolveLatestDateFromArray = (dates) => {
    if (!Array.isArray(dates) || dates.length === 0) return null;
    let latest = null;
    for (const d of dates) {
        const key = normalizeDateKey(d);
        if (key && (/^\d{4}-\d{2}-\d{2}$/.test(key))) {
            if (!latest || key > latest) {
                latest = key;
            }
        }
    }
    return latest;
};

/**
 * Extracts a normalized YYYY-MM-DD date string representing the concluding (end) date
 * of an event program.
 * Checks endDate, latest scheduled date in dates array, date, and startDate.
 *
 * @param {Object} item
 * @returns {string|null} YYYY-MM-DD or null
 */
export const getEventEndDateString = (item) => {
    if (!item || typeof item !== 'object') return null;

    return (
        normalizeDateKey(item.endDate) ||
        resolveLatestDateFromArray(item.dates) ||
        normalizeDateKey(typeof item.dates === 'string' ? item.dates : null) ||
        normalizeDateKey(item.date) ||
        normalizeDateKey(item.startDate) ||
        null
    );
};

